// Admin booking detail (spec §6.2): timeline, fee/balance legs side by side,
// Stripe links, refund under policy, dispute-kit export, no-show/cancel
// actions, host strike.
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction, EdgeFunctionError } from '@/lib/api/edgeFunction';
import { adminMutate } from '@/lib/api/adminMutate';
import { toReason } from '@/lib/api/reason';
import { formatNad } from '@/lib/money/format';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/bookings/$id')({
  component: BookingDetail,
});

interface BookingDetailResponse {
  booking: Record<string, unknown> & { id: string; reference_number: string | null; status: string };
  displayStatus: string | null;
  flags: Record<string, boolean> | null;
  money: {
    totalCents: number;
    fee: { chargedCents: number; refundedCents: number; heldCents: number; paymentStatus: string; feeStatus: string };
    balance: { owedCents: number };
  };
  business: { id: string; name: string | null } | null;
  guest: { id: string; displayName: string | null; email: string | null };
  payments: Array<{ id: string; amount: number; status: string; created_at: string }>;
  refunds: Array<{ id: string; amount_nad: number; status: string; reason: string; created_at: string; credit_note_ref: string | null }>;
  events: Array<{ id: string; event: string; created_at: string; actor_type: string; amount_nad: number | null }>;
  handoffs: unknown[];
  commission: { commission_amount: number; commission_rate: number } | null;
  strikes: Array<{ id: string; kind: string; reason: string; created_at: string }>;
  stripeLinks: { payments: Array<{ id: string; url: string | null }>; refunds: Array<{ id: string; url: string | null }> };
}

interface RefundPreview {
  policyAmountNad: number;
  policyPct: number;
  /** Phase 22: the booking's refund preset and its display label (admin-refund
   * preview). Absent from an older deployed function — the UI falls back to
   * 'Moderate'. */
  policy?: string;
  policyLabel?: string;
  collected: number;
  refunded: number;
  remaining: number;
}

function useBookingDetail(id: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-bookings', 'detail', id],
    queryFn: () => callEdgeFunction<BookingDetailResponse>(supabase, 'admin-bookings', { id }),
  });
}

function BookingDetail() {
  const { id } = Route.useParams();
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useBookingDetail(id);

  const [refundReason, setRefundReason] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');
  const [statusUnknown, setStatusUnknown] = useState(false);
  // Fix round 1, item 3: ONE clientRequestId per refund attempt, generated
  // once (not on every submit) and rotated only after a SUCCESSFUL refund —
  // a retry after a network blip or a transient failure resubmits the SAME
  // logical request (so admin-refund's replay path dedupes it), never a
  // fresh one that would bypass idempotency and risk a double refund.
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  // Fix round 2: after a FAILED attempt, changing the override amount but
  // resubmitting the SAME clientRequestId would hit admin-refund's replay
  // path, which returns the amount stored on the first (failed) attempt —
  // the new amount would be silently ignored. Track what was last
  // submitted; once it stops matching the current input, block Refund and
  // require an explicit "Start a new refund" (which rotates the id).
  const [refundFailed, setRefundFailed] = useState(false);
  const [lastSubmittedAmount, setLastSubmittedAmount] = useState<string | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [strikeReason, setStrikeReason] = useState('');
  const [disputeReason, setDisputeReason] = useState('');

  const previewQuery = useQuery({
    queryKey: ['admin-refund', 'preview', id],
    queryFn: () => callEdgeFunction<RefundPreview>(supabase, 'admin-refund', { bookingId: id, preview: true }),
    enabled: !!data && data.money.fee.chargedCents > data.money.fee.refundedCents,
  });

  const refundMutation = useMutation({
    mutationFn: async () => {
      const reason = toReason(refundReason);
      if (!reason) throw new Error('A reason of at least 10 characters is required.');
      setLastSubmittedAmount(overrideAmount);
      const amountNad = overrideAmount.trim() ? Number(overrideAmount) : undefined;
      return adminMutate(
        supabase,
        'admin-refund',
        { bookingId: id, clientRequestId, amountNad },
        reason,
      );
    },
    onSuccess: () => {
      setRefundReason('');
      setOverrideAmount('');
      setRefundFailed(false);
      setLastSubmittedAmount(null);
      setClientRequestId(crypto.randomUUID()); // rotate — only now is a NEW refund a new logical request
      cancelThenInvalidate(queryClient, { queryKey: ['admin-bookings', 'detail', id] });
      cancelThenInvalidate(queryClient, { queryKey: ['admin-refund', 'preview', id] });
    },
    onError: (err) => {
      // T21/controller note: a 503 means Stripe's own status is unknown —
      // retrying with a NEW request could double-refund. Disable retry
      // rather than let the admin resubmit blind.
      if (err instanceof EdgeFunctionError && err.status === 503) setStatusUnknown(true);
      setRefundFailed(true);
    },
  });

  function startNewRefund() {
    setClientRequestId(crypto.randomUUID());
    setRefundFailed(false);
    setLastSubmittedAmount(null);
  }

  const amountChangedSinceFailure = refundFailed && overrideAmount !== (lastSubmittedAmount ?? '');

  const bookingActionMutation = useMutation({
    mutationFn: async (action: 'mark_no_show' | 'undo_no_show' | 'cancel_system' | 'cancel_host') => {
      const reason = toReason(actionReason);
      if (!reason) throw new Error('A reason of at least 10 characters is required.');
      return adminMutate(supabase, 'admin-booking-action', { bookingId: id, action }, reason);
    },
    onSuccess: () => {
      setActionReason('');
      cancelThenInvalidate(queryClient, { queryKey: ['admin-bookings', 'detail', id] });
      cancelThenInvalidate(queryClient, { queryKey: ['admin-refund', 'preview', id] });
    },
  });

  const strikeMutation = useMutation({
    mutationFn: async () => {
      const reason = toReason(strikeReason);
      if (!reason) throw new Error('A reason of at least 10 characters is required.');
      if (!data?.business?.id) throw new Error('No business on this booking.');
      return adminMutate(supabase, 'admin-strike', { action: 'add', businessId: data.business.id, bookingId: id }, reason);
    },
    onSuccess: () => {
      setStrikeReason('');
      cancelThenInvalidate(queryClient, { queryKey: ['admin-bookings', 'detail', id] });
    },
  });

  const disputeKitMutation = useMutation({
    mutationFn: async () => {
      const reason = toReason(disputeReason);
      if (!reason) throw new Error('A reason of at least 10 characters is required.');
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      if (!token || !supabaseUrl || !anonKey) throw new Error('Not signed in.');
      const res = await fetch(`${supabaseUrl}/functions/v1/admin-dispute-kit`, {
        method: 'POST',
        // Kong's local gateway routes on `apikey` even when a Bearer token
        // is also present (item 8, fix round 1) — the export flow
        // (guests.$id.tsx) already needs the same header; this is the same
        // fix applied here.
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: anonKey },
        body: JSON.stringify({ bookingId: id, reason }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? body?.error ?? `Dispute kit export failed (${res.status})`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `dispute-kit-${data?.booking.reference_number ?? id}.zip`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    },
    onSuccess: () => setDisputeReason(''),
  });

  if (isLoading) return <PageLoading />;
  if (error || !data) {
    return <p className="text-body text-danger">Couldn't load this booking: {(error as Error)?.message}</p>;
  }

  const { booking, money, business, guest, payments, refunds, events, commission, strikes, stripeLinks } = data;

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">
          {booking.reference_number ?? booking.id}
        </h1>
        <p className="mt-xs text-body text-ink-2">
          {data.displayStatus ?? booking.status} · {business?.name ?? 'Unknown business'} · {guest.displayName ?? guest.email ?? '—'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">In-app leg</h2>
          {/* C1: the fee, or the whole price when the guest paid in full in-app. */}
          <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
            <dt className="text-ink-2">Charged</dt>
            <dd className="text-ink">{formatNad(money.fee.chargedCents / 100)}</dd>
            <dt className="text-ink-2">Refunded</dt>
            <dd className="text-ink">{formatNad(money.fee.refundedCents / 100)}</dd>
            <dt className="text-ink-2">Held</dt>
            <dd className="font-semibold text-ink">{formatNad(money.fee.heldCents / 100)}</dd>
            <dt className="text-ink-2">Payment status</dt>
            <dd className="text-ink">{money.fee.paymentStatus}</dd>
          </dl>
          {stripeLinks.payments.map((p) =>
            p.url ? (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="mt-sm block text-caption text-canyon underline">
                Stripe payment {p.id}
              </a>
            ) : null,
          )}
        </div>
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Balance leg</h2>
          <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
            <dt className="text-ink-2">Total</dt>
            <dd className="text-ink">{formatNad(money.totalCents / 100)}</dd>
            <dt className="text-ink-2">Balance owed (desk)</dt>
            <dd className="font-semibold text-ink">{formatNad(money.balance.owedCents / 100)}</dd>
            {commission ? (
              <>
                <dt className="text-ink-2">Commission</dt>
                <dd className="text-ink">
                  {/* commission_rate is stored as a fraction (0.100 = 10%) — ×100 for display (minor fix, round 1) */}
                  {formatNad(commission.commission_amount)} ({(commission.commission_rate * 100).toFixed(1)}%)
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Refund</h2>
        {statusUnknown ? (
          <p className="mt-sm rounded-button bg-tint-warning p-sm text-body text-danger">
            The last refund attempt's outcome could not be confirmed with Stripe. Do not retry with a new
            request — check the Stripe dashboard and this booking's refund history before trying again.
          </p>
        ) : (
          <>
            {previewQuery.data ? (
              <p className="mt-sm text-body text-ink-2">
                Policy amount: <span className="font-semibold text-ink">{formatNad(previewQuery.data.policyAmountNad)}</span>{' '}
                ({previewQuery.data.policyLabel ?? 'Moderate'} policy, {previewQuery.data.policyPct}%) · remaining refundable: {formatNad(previewQuery.data.remaining)}
              </p>
            ) : money.fee.chargedCents <= money.fee.refundedCents ? (
              <p className="mt-sm text-body text-ink-2">Nothing left to refund.</p>
            ) : null}
            <div className="mt-md flex flex-col gap-sm md:max-w-md">
              <label className="text-caption font-semibold text-ink-2" htmlFor="refund-override">
                Override amount (N$, optional — defaults to the policy amount)
              </label>
              <input
                id="refund-override"
                type="number"
                step="0.01"
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
                className="rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
              />
              <label className="text-caption font-semibold text-ink-2" htmlFor="refund-reason">
                Reason (required — a non-policy amount is logged as an override)
              </label>
              <textarea
                id="refund-reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="min-h-[64px] rounded-button border border-border bg-surface p-sm text-body text-ink"
              />
              {amountChangedSinceFailure ? (
                <p className="text-body text-danger">
                  The amount changed after a failed attempt — start a new refund before retrying (the previous
                  attempt's request id can't be reused with a different amount).
                </p>
              ) : null}
              <div className="flex gap-sm">
                <button
                  type="button"
                  disabled={
                    refundMutation.isPending || money.fee.chargedCents <= money.fee.refundedCents || amountChangedSinceFailure
                  }
                  onClick={() => refundMutation.mutate()}
                  className="rounded-button bg-canyon px-md py-sm text-body font-semibold text-white disabled:opacity-50"
                >
                  Refund
                </button>
                {refundFailed ? (
                  <button
                    type="button"
                    onClick={startNewRefund}
                    className="rounded-button border border-border px-md py-sm text-body text-ink"
                  >
                    Start a new refund
                  </button>
                ) : null}
              </div>
              {refundMutation.isError ? (
                <p className="text-body text-danger">{(refundMutation.error as Error).message}</p>
              ) : null}
            </div>
          </>
        )}
        {refunds.length > 0 ? (
          <ul className="mt-md flex flex-col gap-xs">
            {refunds.map((r) => (
              <li key={r.id} className="text-caption text-ink-2">
                {formatNad(r.amount_nad)} · {r.status} · {r.credit_note_ref ?? '—'} · {new Date(r.created_at).toLocaleString()}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Timeline</h2>
        <ul className="mt-md flex flex-col gap-xs">
          {events.map((e) => (
            <li key={e.id} className="flex items-center justify-between text-body text-ink">
              <span>{e.event}</span>
              <span className="text-caption text-ink-2">{new Date(e.created_at).toLocaleString()}</span>
            </li>
          ))}
          {events.length === 0 ? <li className="text-body text-ink-2">No events yet.</li> : null}
        </ul>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Booking actions</h2>
        <div className="mt-md flex flex-col gap-sm md:max-w-md">
          <textarea
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Reason (required)…"
            className="min-h-[52px] rounded-button border border-border bg-surface p-sm text-body text-ink"
          />
          <div className="flex flex-wrap gap-sm">
            <button
              type="button"
              disabled={bookingActionMutation.isPending}
              onClick={() => bookingActionMutation.mutate('mark_no_show')}
              className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
            >
              Mark no-show
            </button>
            <button
              type="button"
              disabled={bookingActionMutation.isPending}
              onClick={() => bookingActionMutation.mutate('undo_no_show')}
              className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
            >
              Undo no-show
            </button>
            <button
              type="button"
              disabled={bookingActionMutation.isPending}
              onClick={() => bookingActionMutation.mutate('cancel_system')}
              className="rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
            >
              Cancel (nothing collected)
            </button>
            <button
              type="button"
              disabled={bookingActionMutation.isPending || !toReason(actionReason)}
              onClick={() => bookingActionMutation.mutate('cancel_host')}
              title="Cancels on the host's behalf: 100% fee refund + a host strike, guest notified"
              className="rounded-button border border-danger px-md py-sm text-body font-semibold text-danger disabled:opacity-50"
            >
              Cancel on host's behalf (100% refund + strike)
            </button>
          </div>
          {bookingActionMutation.isError ? (
            <p className="text-body text-danger">{(bookingActionMutation.error as Error).message}</p>
          ) : null}
        </div>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Host strikes</h2>
        <ul className="mt-sm flex flex-col gap-xs">
          {strikes.map((s) => (
            <li key={s.id} className="text-body text-ink-2">
              {s.kind} — {s.reason} ({new Date(s.created_at).toLocaleDateString()})
            </li>
          ))}
          {strikes.length === 0 ? <li className="text-body text-ink-2">No strikes on this booking.</li> : null}
        </ul>
        <div className="mt-md flex flex-col gap-sm md:max-w-md">
          <textarea
            value={strikeReason}
            onChange={(e) => setStrikeReason(e.target.value)}
            placeholder="Reason for a new strike (required)…"
            className="min-h-[52px] rounded-button border border-border bg-surface p-sm text-body text-ink"
          />
          <button
            type="button"
            disabled={strikeMutation.isPending || !business?.id}
            onClick={() => strikeMutation.mutate()}
            className="w-fit rounded-button bg-danger px-md py-sm text-body font-semibold text-white disabled:opacity-50"
          >
            Add strike
          </button>
        </div>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Dispute kit</h2>
        <p className="mt-xs text-body text-ink-2">
          Exports booking, payments, refunds, events, check-in, hand-offs, messages and the policy text as a zip.
        </p>
        <div className="mt-md flex flex-col gap-sm md:max-w-md">
          <textarea
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            placeholder="Reason for exporting this evidence kit (required)…"
            className="min-h-[52px] rounded-button border border-border bg-surface p-sm text-body text-ink"
          />
          <button
            type="button"
            disabled={disputeKitMutation.isPending || !toReason(disputeReason)}
            onClick={() => disputeKitMutation.mutate()}
            className="w-fit rounded-button border border-border px-md py-sm text-body text-ink disabled:opacity-50"
          >
            Download dispute kit
          </button>
          {disputeKitMutation.isError ? (
            <p className="text-body text-danger">{(disputeKitMutation.error as Error).message}</p>
          ) : null}
        </div>
      </div>

      {payments.length > 0 ? (
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Payments</h2>
          <ul className="mt-sm flex flex-col gap-xs">
            {payments.map((p) => (
              <li key={p.id} className="text-body text-ink-2">
                {formatNad(p.amount)} · {p.status} · {new Date(p.created_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
